/**
 * Stammdaten des Unternehmens.
 *
 * Was hier steht, landet auf jeder Rechnung. Eine falsche IBAN wandert in
 * den GiroCode und fällt erst auf, wenn ein Kunde anruft, weil seine
 * Überweisung zurückkam — deshalb prüft die API IBAN und BIC und weist
 * ungültige ab, statt sie anzunehmen.
 *
 * Geschäftsjahr und Nummernkreise stehen ebenfalls hier: Ohne
 * Geschäftsjahr lässt sich nichts buchen, und ohne Nummernkreis keine
 * Nummer vergeben. Beides entsteht gemeinsam.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Ankreuzfeld, Textbereich, Textfeld } from '../bausteine/Feld';
import { Seite } from '../bausteine/Layout';
import { Fehlermeldung, Meldung } from '../bausteine/Meldung';
import { Laedt } from '../bausteine/Zustand';
import { useAbruf, useVorgang } from '../lib/abruf';
import { api } from '../lib/api';
import { datum, jahrHeute } from '../lib/format';
import { useSitzung } from '../sitzung/SitzungKontext';
import type { Geschaeftsjahr, Nummernkreis, Unternehmen } from '../lib/typen';

export function Stammdaten(): ReactNode {
  const { benutzer } = useSitzung();
  const darfAendern = benutzer?.rolle === 'inhaber';

  const unternehmen = useAbruf<Unternehmen>('/api/unternehmen');
  const jahre = useAbruf<Geschaeftsjahr[]>('/api/geschaeftsjahre');
  const kreise = useAbruf<Nummernkreis[]>('/api/nummernkreise');

  return (
    <Seite titel="Stammdaten" untertitel={unternehmen.daten?.name}>
      <div className="stapel" style={{ maxWidth: '1000px' }}>
        {unternehmen.fehler ? <Fehlermeldung text={unternehmen.fehler} /> : null}

        {!darfAendern ? (
          <Meldung ton="info">Stammdaten darf nur der Inhaber ändern.</Meldung>
        ) : null}

        {unternehmen.daten && !unternehmen.daten.rechnungsfaehig ? (
          <Meldung ton="warnung" titel="Für eine Rechnung fehlt noch etwas.">
            {unternehmen.daten.fehlendeAngaben.join(', ')}
          </Meldung>
        ) : null}

        {!unternehmen.daten ? (
          <div className="karte">
            <Laedt />
          </div>
        ) : (
          <UnternehmensFormular
            unternehmen={unternehmen.daten}
            darfAendern={darfAendern}
            neuLaden={unternehmen.neuLaden}
          />
        )}

        <Geschaeftsjahre jahre={jahre} kreise={kreise} darfAendern={darfAendern} />
      </div>
    </Seite>
  );
}

// =========================================================================
//  Unternehmen
// =========================================================================

function UnternehmensFormular({
  unternehmen,
  darfAendern,
  neuLaden,
}: {
  unternehmen: Unternehmen;
  darfAendern: boolean;
  neuLaden: () => void;
}): ReactNode {
  const vorgang = useVorgang();
  const [gespeichert, setzeGespeichert] = useState(false);
  const [felder, setzeFelder] = useState(unternehmen);

  // Nach dem Neuladen sollen die Felder zeigen, was der Server bestätigt hat
  // — vor allem bei der IBAN, die er normalisiert zurückgibt.
  useEffect(() => setzeFelder(unternehmen), [unternehmen]);

  function setze<K extends keyof Unternehmen>(feld: K, wert: Unternehmen[K]): void {
    setzeFelder((alt) => ({ ...alt, [feld]: wert }));
    setzeGespeichert(false);
  }

  async function speichern(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const oderNull = (wert: string | null): string | null =>
      wert === null || wert.trim() === '' ? null : wert.trim();

    const ergebnis = await vorgang.fuehreAus(() =>
      api.aendern('/api/unternehmen', {
        name: felder.name.trim(),
        inhaberName: felder.inhaberName.trim(),
        rechtsform: felder.rechtsform.trim(),
        strasse: felder.strasse.trim(),
        plz: felder.plz.trim(),
        ort: felder.ort.trim(),
        land: felder.land.trim().toUpperCase(),
        steuernummer: oderNull(felder.steuernummer),
        ustId: oderNull(felder.ustId),
        finanzamt: oderNull(felder.finanzamt),
        kleinunternehmer: felder.kleinunternehmer,
        telefon: oderNull(felder.telefon),
        email: oderNull(felder.email),
        website: oderNull(felder.website),
        bankName: oderNull(felder.bankName),
        iban: oderNull(felder.iban),
        bic: oderNull(felder.bic),
        rechnungZahlungszielTage: felder.rechnungZahlungszielTage,
        rechnungEinleitungstext: oderNull(felder.rechnungEinleitungstext),
        rechnungSchlusstext: oderNull(felder.rechnungSchlusstext),
      }),
    );
    if (ergebnis !== undefined) {
      setzeGespeichert(true);
      neuLaden();
    }
  }

  async function logoHochladen(datei: File): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.sendeDatei('/api/unternehmen/logo', datei),
    );
    if (ergebnis !== undefined) neuLaden();
  }

  return (
    <form className="stapel" onSubmit={(e) => void speichern(e)}>
      <Fehlermeldung text={vorgang.fehler} />
      {gespeichert ? <Meldung ton="gut">Stammdaten gespeichert.</Meldung> : null}

      <div className="karte">
        <div className="karte-kopf">
          <h2>Unternehmen</h2>
        </div>
        <div className="karte-koerper formularraster">
          <Textfeld
            beschriftung="Name"
            wert={felder.name}
            setzeWert={(w) => setze('name', w)}
            pflicht
            breit
            gesperrt={!darfAendern}
          />
          <Textfeld
            beschriftung="Inhaber"
            wert={felder.inhaberName}
            setzeWert={(w) => setze('inhaberName', w)}
            pflicht
            gesperrt={!darfAendern}
          />
          <Textfeld
            beschriftung="Rechtsform"
            wert={felder.rechtsform}
            setzeWert={(w) => setze('rechtsform', w)}
            gesperrt={!darfAendern}
          />
          <Textfeld
            beschriftung="Straße und Nummer"
            wert={felder.strasse}
            setzeWert={(w) => setze('strasse', w)}
            pflicht
            breit
            gesperrt={!darfAendern}
          />
          <Textfeld
            beschriftung="PLZ"
            wert={felder.plz}
            setzeWert={(w) => setze('plz', w)}
            pflicht
            gesperrt={!darfAendern}
          />
          <Textfeld
            beschriftung="Ort"
            wert={felder.ort}
            setzeWert={(w) => setze('ort', w)}
            pflicht
            gesperrt={!darfAendern}
          />
          <Textfeld
            beschriftung="Land"
            wert={felder.land}
            setzeWert={(w) => setze('land', w)}
            maxLaenge={2}
            gesperrt={!darfAendern}
          />
        </div>
      </div>

      <div className="karte">
        <div className="karte-kopf">
          <h2>Steuerliches</h2>
        </div>
        <div className="karte-koerper stapel">
          <div className="formularraster">
            <Textfeld
              beschriftung="Steuernummer"
              wert={felder.steuernummer ?? ''}
              setzeWert={(w) => setze('steuernummer', w)}
              gesperrt={!darfAendern}
              hinweis="Steuernummer oder USt-IdNr. gehört auf die Rechnung"
            />
            <Textfeld
              beschriftung="USt-IdNr."
              wert={felder.ustId ?? ''}
              setzeWert={(w) => setze('ustId', w)}
              gesperrt={!darfAendern}
            />
            <Textfeld
              beschriftung="Finanzamt"
              wert={felder.finanzamt ?? ''}
              setzeWert={(w) => setze('finanzamt', w)}
              gesperrt={!darfAendern}
            />
          </div>

          <Ankreuzfeld
            beschriftung="Kleinunternehmerregelung nach § 19 UStG anwenden"
            wert={felder.kleinunternehmer}
            setzeWert={(w) => setze('kleinunternehmer', w)}
            gesperrt={!darfAendern}
          />

          {felder.kleinunternehmer ? (
            <p className="klein still" style={{ margin: 0 }}>
              Rechnungen weisen keine Umsatzsteuer aus und tragen den Hinweis auf
              § 19 UStG. Ob die Regelung im Einzelfall noch gilt, entscheidet der
              Umsatz — siehe EÜR.
            </p>
          ) : (
            <Meldung ton="warnung">
              Ohne Kleinunternehmerregelung müssen Rechnungen Umsatzsteuer
              ausweisen, und es sind Voranmeldungen fällig. Diese Software
              unterstützt das nicht vollständig — das gehört zum Steuerberater.
            </Meldung>
          )}
        </div>
      </div>

      <div className="karte">
        <div className="karte-kopf">
          <h2>Bankverbindung</h2>
          <span className="klein still">für Rechnung und GiroCode</span>
        </div>
        <div className="karte-koerper formularraster">
          <Textfeld
            beschriftung="Bank"
            wert={felder.bankName ?? ''}
            setzeWert={(w) => setze('bankName', w)}
            gesperrt={!darfAendern}
          />
          <Textfeld
            beschriftung="IBAN"
            wert={felder.iban ?? ''}
            setzeWert={(w) => setze('iban', w)}
            gesperrt={!darfAendern}
            hinweis="Prüfziffer wird geprüft; Leerzeichen sind erlaubt"
          />
          <Textfeld
            beschriftung="BIC"
            wert={felder.bic ?? ''}
            setzeWert={(w) => setze('bic', w)}
            gesperrt={!darfAendern}
          />
        </div>
      </div>

      <div className="karte">
        <div className="karte-kopf">
          <h2>Kontakt und Rechnungstexte</h2>
        </div>
        <div className="karte-koerper formularraster">
          <Textfeld
            beschriftung="Telefon"
            art="tel"
            wert={felder.telefon ?? ''}
            setzeWert={(w) => setze('telefon', w)}
            gesperrt={!darfAendern}
          />
          <Textfeld
            beschriftung="E-Mail"
            art="email"
            wert={felder.email ?? ''}
            setzeWert={(w) => setze('email', w)}
            gesperrt={!darfAendern}
          />
          <Textfeld
            beschriftung="Website"
            wert={felder.website ?? ''}
            setzeWert={(w) => setze('website', w)}
            gesperrt={!darfAendern}
          />
          <Textfeld
            beschriftung="Zahlungsziel in Tagen"
            art="number"
            wert={String(felder.rechnungZahlungszielTage)}
            setzeWert={(w) => setze('rechnungZahlungszielTage', Number.parseInt(w, 10) || 0)}
            klasse="zahl"
            gesperrt={!darfAendern}
            hinweis="Vorgabe für neue Rechnungen"
          />
          <Textbereich
            beschriftung="Einleitungstext"
            wert={felder.rechnungEinleitungstext ?? ''}
            setzeWert={(w) => setze('rechnungEinleitungstext', w)}
            zeilen={2}
            breit
            gesperrt={!darfAendern}
          />
          <Textbereich
            beschriftung="Schlusstext"
            wert={felder.rechnungSchlusstext ?? ''}
            setzeWert={(w) => setze('rechnungSchlusstext', w)}
            zeilen={2}
            breit
            gesperrt={!darfAendern}
          />
        </div>
      </div>

      <div className="karte">
        <div className="karte-kopf">
          <h2>Logo</h2>
          <span className="klein still">PNG oder JPEG, höchstens 4 MB</span>
        </div>
        <div className="karte-koerper stapel">
          {unternehmen.logoPfad ? (
            <p className="klein still" style={{ margin: 0 }}>
              Hinterlegt: <span className="fest">{unternehmen.logoPfad}</span>
            </p>
          ) : (
            <p className="klein still" style={{ margin: 0 }}>
              Noch kein Logo. Die Rechnung entsteht auch ohne — dann nur mit
              der Absenderzeile.
            </p>
          )}
          {darfAendern ? (
            <input
              type="file"
              accept="image/png,image/jpeg"
              disabled={vorgang.laeuft}
              onChange={(e) => {
                const datei = e.target.files?.[0];
                if (datei) void logoHochladen(datei);
                e.target.value = '';
              }}
            />
          ) : null}
          <p className="klein still" style={{ margin: 0 }}>
            PNG mit transparentem Hintergrund sieht auf der Rechnung am besten
            aus.
          </p>
        </div>
      </div>

      {darfAendern ? (
        <div className="reihe">
          <button type="submit" className="knopf haupt" disabled={vorgang.laeuft}>
            {vorgang.laeuft ? 'Wird gespeichert …' : 'Stammdaten speichern'}
          </button>
        </div>
      ) : null}
    </form>
  );
}

// =========================================================================
//  Geschäftsjahre und Nummernkreise
// =========================================================================

function Geschaeftsjahre({
  jahre,
  kreise,
  darfAendern,
}: {
  jahre: ReturnType<typeof useAbruf<Geschaeftsjahr[]>>;
  kreise: ReturnType<typeof useAbruf<Nummernkreis[]>>;
  darfAendern: boolean;
}): ReactNode {
  const vorgang = useVorgang();
  const vorhanden = new Set((jahre.daten ?? []).map((j) => j.jahr));
  const naechstes = [jahrHeute(), jahrHeute() + 1].find((j) => !vorhanden.has(j));

  async function anlegen(jahr: number): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden('/api/geschaeftsjahre', { jahr }),
    );
    if (ergebnis !== undefined) {
      jahre.neuLaden();
      kreise.neuLaden();
    }
  }

  return (
    <div className="karte">
      <div className="karte-kopf">
        <h2>Geschäftsjahre und Nummernkreise</h2>
        {darfAendern && naechstes !== undefined ? (
          <button
            type="button"
            className="knopf klein"
            disabled={vorgang.laeuft}
            onClick={() => void anlegen(naechstes)}
          >
            Geschäftsjahr {naechstes} anlegen
          </button>
        ) : null}
      </div>

      <div className="karte-koerper stapel">
        <Fehlermeldung text={vorgang.fehler} />

        {!jahre.daten ? (
          <Laedt />
        ) : jahre.daten.length === 0 ? (
          <Meldung ton="warnung" titel="Kein Geschäftsjahr angelegt.">
            Ohne Geschäftsjahr lässt sich kein Beleg buchen. Beim Anlegen
            entstehen zugleich die Nummernkreise für Belege, Rechnungen und
            Angebote.
          </Meldung>
        ) : (
          <div className="tabelle-rahmen">
            <table className="tabelle">
              <thead>
                <tr>
                  <th className="schmal">Jahr</th>
                  <th className="schmal">Zeitraum</th>
                  <th className="schmal">Status</th>
                  <th>Nummernkreise</th>
                </tr>
              </thead>
              <tbody>
                {jahre.daten.map((j) => (
                  <tr key={j.id}>
                    <td className="schmal">
                      <strong>{j.jahr}</strong>
                    </td>
                    <td className="schmal klein still">
                      {datum(j.beginntAm)} – {datum(j.endetAm)}
                    </td>
                    <td className="schmal">
                      {j.festgeschriebenAm ? (
                        <span className="marker gut">festgeschrieben</span>
                      ) : (
                        <span className="marker">offen</span>
                      )}
                    </td>
                    <td className="klein">
                      {(kreise.daten ?? [])
                        .filter((k) => k.jahr === j.jahr)
                        .map((k) => (
                          <div key={k.id}>
                            <span className="fest">
                              {k.praefix}
                              {String(k.naechsteNummer).padStart(k.stellen, '0')}
                            </span>{' '}
                            <span className="still">
                              — nächste {k.art === 'beleg' ? 'Belegnummer' : k.art === 'rechnung' ? 'Rechnungsnummer' : 'Angebotsnummer'}
                            </span>
                          </div>
                        ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="klein still" style={{ margin: 0 }}>
          Nummernkreise lassen sich nur vorwärts drehen. Eine bereits vergebene
          Nummer ein zweites Mal auszugeben wäre ein Verstoß gegen § 14 UStG —
          deshalb weist die API das Zurücksetzen ab.
        </p>
      </div>
    </div>
  );
}
